import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { ApiProgress } from "../../Utilities/ApiProgress";
import { useDistributorSettingsContext } from "./context/DistributorSettingsContext";
import { DistributorSettingsSkuGrid } from "./components/DistributorSettingsSkuGrid";
import InfoDialog from '../ProductSettings/dialogs/InfoDialog';
import ErrorDialog from '../ProductSettings/dialogs/ErrorDialog';
import FileUpload from "../../Components/FileUpload";
import TicketRequestMenu from "../../Components/TicketRequestMenu";
import { toYyyyMmDdInput } from '../ProductSettings/utils/inMarketDate';
import { enableInMarketDate, enableSeasonalForecast } from '../../Utilities/settingsGridColumns';

const getProductSettingsUploadTemplateCsv = () =>
    process.env.REACT_APP_ENABLE_IN_MARKET_DATE === 'true'
        ? 'distributor,sku,offeringStatus,fulfillmentMethod,directDistributor,palletQuantity,palletDivisor,unitWeight,inMarketDate\n'
        : 'distributor,sku,offeringStatus,fulfillmentMethod,directDistributor,palletQuantity,palletDivisor,unitWeight\n';

const EXPORT_COLUMNS = [
    { key: 'sku', header: 'SKU' },
    { key: 'description', header: 'Description' },
    { key: 'legal', header: 'Legal' },
    { key: 'palletQuantity', header: 'Pallet Quantity' },
    { key: 'palletDivisor', header: 'Pallet Divisor' },
    { key: 'unitWeight', header: 'Unit Weight' },
    { key: 'offeringStatus', header: 'Offering Status' },
    { key: 'fulfillmentMethod', header: 'Fulfillment Method' },
    { key: 'customerPartNumber', header: 'Customer Part #' },
    ...(enableSeasonalForecast ? [{ key: 'seasonal', header: 'Seasonal' }] : []),
    ...(enableInMarketDate ? [{ key: 'inMarketDate', header: 'In-Market Date' }] : []),
    ...(enableInMarketDate && enableSeasonalForecast ? [{ key: 'endInMarketDate', header: 'End In-Market Date' }] : []),
];

const toExportDate = (yyyyMmDd) => (yyyyMmDd ? dayjs(yyyyMmDd).format('MM/DD/YYYY') : '');

const toExportRow = (row) => ({
    sku: row.product?.sku ?? '',
    description: row.product?.description ?? '',
    legal: row.legal === false ? 'No' : 'Yes',
    palletQuantity: row.palletQuantity ?? '',
    palletDivisor: row.palletDivisor ?? '',
    unitWeight: row.unitWeight ?? '',
    offeringStatus: row.skuOfferingStatus?.description ?? '',
    fulfillmentMethod: row.skuFulfillmentType
        ? `${row.skuFulfillmentType.description}${row.indirectDistributorName ? ` (via ${row.indirectDistributorName})` : ''}`
        : '',
    customerPartNumber: row.customerPartNumber ?? '',
    seasonal: row.seasonal ? 'Yes' : '',
    inMarketDate: toExportDate(toYyyyMmDdInput(row.inMarketDateOverride) || toYyyyMmDdInput(row.inMarketDateDefault)),
    endInMarketDate: toExportDate(toYyyyMmDdInput(row.endInMarketDateOverride) || toYyyyMmDdInput(row.endInMarketDateDefault)),
});

const DistributorSettings = () => {
    const {
        dataLoaded,
        selectedDistributor,
        skuTable,
        inMarketDateError,
        clearInMarketDateError,
    } = useDistributorSettingsContext();

    const handleExportClick = () => {
        const rows = skuTable.map(toExportRow);
        const headerRow = EXPORT_COLUMNS.map(column => column.header);
        const dataRows = rows.map(row => EXPORT_COLUMNS.map(column => row[column.key]));

        const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
        worksheet['!cols'] = EXPORT_COLUMNS.map((column, columnIndex) => ({
            wch: Math.max(column.header.length, ...dataRows.map(row => String(row[columnIndex] ?? '').length)) + 2,
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Distributor Items');

        const dateStamp = dayjs().format('YYYYMMDD');
        const filename = `OBO_Distributor_Items_${selectedDistributor?.sapId ?? ''}_${dateStamp}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (dataLoaded ? <Paper elevation={1} sx={{ padding: 2, margin: 2 }}>
        <Stack direction='column' spacing={1}>
            <Grid container>
                <Grid item xs={6}>
                    <Typography
                        variant='h4'>{selectedDistributor.sapId} - {selectedDistributor.name}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'nowrap' }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleExportClick}
                        sx={{ whiteSpace: 'nowrap', mt: '-5px' }}>
                        Export Settings
                    </Button>
                    <FileUpload
                        uploadUrl="api/products/productSettings"
                        buttonText="Upload Settings"
                        accept=".csv"
                        InfoDialogComponent={InfoDialog}
                        ErrorDialogComponent={ErrorDialog}
                        templateDownloadFileName="ProductSettingsUploadTemplate.csv"
                        templateDownloadTooltip="Download template"
                        getTemplateDownloadContent={getProductSettingsUploadTemplateCsv}
                    />
                    <TicketRequestMenu />
                </Grid>
            </Grid>
            <div style={{ width: "100%", height: "70vh" }}>
                <DistributorSettingsSkuGrid />
            </div>
        </Stack>
        <Dialog open={!!inMarketDateError} onClose={clearInMarketDateError}>
            <DialogTitle>Invalid In-Market Date</DialogTitle>
            <DialogContent>
                <Typography>{inMarketDateError}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={clearInMarketDateError} autoFocus>OK</Button>
            </DialogActions>
        </Dialog>
    </Paper> : <ApiProgress />)
}

export default DistributorSettings;
